import { apiClient } from '@/utils/api-client';

export interface BatchUploadStatus {
  id: string;
  status: 'pending' | 'in_progress' | 'completed' | 'completed_with_errors' | 'failed';
  file_key: string;
  job_type: 'upload' | 'delete';
  total_count?: number;
  processed_count?: number;
  percentage_complete: number;
  errors?: string;
  completed_at?: string;
  created_at: string;
  has_errors: boolean;
  is_completed: boolean;
}

export interface UploadUrlResponse {
  upload_url: string;
  key: string;
}

export const batchUploadService = {
  // Download template with selected products
  async downloadTemplate(productIds?: string[]) {
    const params = productIds?.length ? { product_ids: productIds.join(',') } : {};

    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/admin/batch_uploads/download_template`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include session cookies for authentication
      body: JSON.stringify(params),
    });
    
    if (!response.ok) {
      throw new Error('Failed to download template');
    }
    
    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    const filename = contentDisposition?.split('filename=')[1]?.replace(/"/g, '') || 'template.csv';
    
    // Download file
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },

  // Download Excel-compatible template with selected products
  // This template includes a "sep=," line that tells Excel to parse comma-delimited values correctly
  async downloadExcelTemplate(productIds?: string[]) {
    const params = productIds?.length ? { product_ids: productIds.join(',') } : {};

    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/admin/batch_uploads/download_excel_template`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      throw new Error('Failed to download Excel template');
    }

    const contentDisposition = response.headers.get('Content-Disposition');
    const filename = contentDisposition?.split('filename=')[1]?.replace(/"/g, '') || 'template.csv';

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },

  // Get presigned URL for S3 upload
  async getUploadUrl(): Promise<UploadUrlResponse> {
    return apiClient.post('/api/admin/batch_uploads/create_upload_url');
  },
  
  // Upload file to S3
  async uploadToS3(file: File, uploadUrl: string): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': 'text/csv',
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to upload file to S3');
    }
  },
  
  // Process uploaded file
  async processUpload(fileKey: string, sendEmails: boolean = false) {
    return apiClient.post('/api/admin/batch_uploads/process_upload', {
      file_key: fileKey,
      send_emails: sendEmails,
    });
  },
  
  // Get job status
  async getJobStatus(): Promise<{ data: BatchUploadStatus[] }> {
    const response = await apiClient.get<{ data: Array<{ id: string; type: string; attributes: BatchUploadStatus }> }>('/api/admin/batch_uploads/job_status');
    // Transform JSON API format to flat array
    return {
      data: response.data.map(item => ({
        ...item.attributes,
        id: item.id,
      }))
    };
  },
  
  // Bulk delete methods
  async downloadDeleteTemplate() {
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/admin/batch_uploads/download_delete_template`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include session cookies for authentication
    });
    
    if (!response.ok) {
      throw new Error('Failed to download delete template');
    }
    
    const contentDisposition = response.headers.get('Content-Disposition');
    const filename = contentDisposition?.split('filename=')[1]?.replace(/"/g, '') || 'delete_template.csv';
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },
  
  async processDelete(fileKey: string) {
    return apiClient.post('/api/admin/batch_uploads/process_delete', {
      file_key: fileKey,
    });
  },
};